import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from './MultiSelectTicker';
import AccordionFormSection from './Screener/AccordionFormSection';
import IntermediatePresetCard from './Screener/IntermediatePresetCard';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { formatTickersForAPI, intermediatePresets, buildIntermediateScenarios } from '../utils/presetMappings';
import { cardToggleTemplate, actionButtonTemplate } from '../theme';
import { Play } from 'lucide-react';

const ScreenerIntermediateForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  hideButtons = false,
  onFormValidityChange
}, ref) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Form values
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null); // 'call' or 'put'
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedStrikes, setSelectedStrikes] = useState([]);

  // Accordion state
  const [expandedSections, setExpandedSections] = useState(
    isMobile ? ['tickers'] : ['tickers', 'direction', 'expiration', 'strike']
  );
  const [completedSections, setCompletedSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('tickers');

  // Expose form state and methods to parent component
  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedExpirations,
      selectedStrikes,
      isValid: isFormValid()
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedExpirations([]);
      setSelectedStrikes([]);
      setCompletedSections(new Set());
      setActiveSection('tickers');
      setExpandedSections(isMobile ? ['tickers'] : ['tickers', 'direction', 'expiration', 'strike']);
    },
    loadScenario: (scenario) => {
      console.log('Loading scenario into intermediate form:', scenario);

      // Set tickers
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : scenario.tickers.split(',').map(t => t.trim());

      const tickerObjects = tickerArray.map(ticker => ({
        value: ticker,
        label: ticker
      }));
      setSelectedTickers(tickerObjects);

      // Set direction - handle both legacy (option_type only) and new (position_type + option_type) formats
      const positionType = scenario.position_type || 'buy'; // Default to 'buy' for legacy scenarios
      const optionType = scenario.option_type; // 'call' or 'put'
      setDirection(`${positionType}_${optionType}`); // e.g., 'buy_call', 'sell_put'

      // Try to match to intermediate presets if _intermediate metadata exists
      if (scenario._intermediate) {
        setSelectedExpirations([scenario._intermediate.expiration]);
        setSelectedStrikes([scenario._intermediate.strike]);
      } else {
        // Try to find closest match based on parameters
        const expMatch = findClosestExpiration(scenario.days_until_exp);
        const strikeMatch = findClosestStrike(scenario.strike_pct, scenario.option_type);

        if (expMatch) setSelectedExpirations([expMatch]);
        if (strikeMatch) setSelectedStrikes([strikeMatch]);
      }

      // Mark sections as completed
      setCompletedSections(new Set(['tickers', 'direction', 'expiration', 'strike']));

      // Expand appropriate sections
      if (isMobile) {
        setExpandedSections(['strike']);
        setActiveSection('strike');
      } else {
        setExpandedSections(['tickers', 'direction', 'expiration', 'strike']);
      }
    }
  }));

  // Helper functions to find closest preset matches
  const findClosestExpiration = (daysUntilExp) => {
    let closest = null;
    let minDiff = Infinity;
    for (const [key, preset] of Object.entries(intermediatePresets.expiration)) {
      const diff = Math.abs(preset.days_until_exp - daysUntilExp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = key;
      }
    }
    return closest;
  };

  const findClosestStrike = (strikePct, optionType) => {
    // For puts, the strike was already adjusted, so we need to un-adjust
    const adjustedStrikePct = optionType === 'put' && strikePct !== 0 ? -strikePct : strikePct;

    let closest = null;
    let minDiff = Infinity;
    for (const [key, preset] of Object.entries(intermediatePresets.strike)) {
      const diff = Math.abs(preset.strike_pct - adjustedStrikePct);
      if (diff < minDiff) {
        minDiff = diff;
        closest = key;
      }
    }
    return closest;
  };

  // Unified function to handle section transitions (mobile)
  const activateNextSection = (currentSection, nextSection = null) => {
    if (!isMobile) return;

    setCompletedSections(prev => new Set(prev).add(currentSection));

    if (nextSection) {
      setExpandedSections([nextSection]);
      setActiveSection(nextSection);
    } else {
      setExpandedSections([]);
      setActiveSection('complete');
    }
  };

  // Auto-advance logic (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    if (selectedTickers.length > 0 && activeSection === 'tickers') {
      activateNextSection('tickers', 'direction');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (direction && activeSection === 'direction') {
      activateNextSection('direction', 'expiration');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (selectedExpirations.length > 0 && activeSection === 'expiration') {
      activateNextSection('expiration', 'strike');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpirations, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (selectedStrikes.length > 0 && activeSection === 'strike') {
      setCompletedSections(prev => new Set(prev).add('strike'));
      setTimeout(() => {
        setExpandedSections([]);
        setActiveSection('complete');
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrikes, isMobile]);

  // Monitor form validity and notify parent
  useEffect(() => {
    if (onFormValidityChange) {
      onFormValidityChange(isFormValid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, direction, selectedExpirations, selectedStrikes, onFormValidityChange]);

  // Helper: Determine if section should be disabled (mobile only)
  const isSectionDisabled = (section) => {
    if (!isMobile) return false;

    if (completedSections.has(section)) return false;

    const sectionOrder = ['tickers', 'direction', 'expiration', 'strike'];
    const sectionIndex = sectionOrder.indexOf(section);

    let highestCompletedIndex = -1;
    sectionOrder.forEach((s, idx) => {
      if (completedSections.has(s)) {
        highestCompletedIndex = Math.max(highestCompletedIndex, idx);
      }
    });

    return sectionIndex > highestCompletedIndex + 1;
  };

  // Handle section toggle
  const handleToggle = (section) => {
    if (isMobile) {
      setExpandedSections(prev => {
        if (prev.includes(section)) {
          return [];
        } else {
          return [section];
        }
      });

      const sectionOrder = ['tickers', 'direction', 'expiration', 'strike'];
      if (sectionOrder.includes(section)) {
        setActiveSection(section);
      }
    } else {
      setExpandedSections(prev =>
        prev.includes(section)
          ? prev.filter(s => s !== section)
          : [...prev, section]
      );
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      selectedTickers.length > 0 &&
      direction !== null &&
      selectedExpirations.length > 0 &&
      selectedStrikes.length > 0
    );
  };

  // Generate tooltip message for disabled button
  const getDisabledTooltipMessage = () => {
    const missingItems = [];

    if (selectedTickers.length === 0) missingItems.push('Tickers');
    if (direction === null) missingItems.push('Direction');
    if (selectedExpirations.length === 0) missingItems.push('Expiration');
    if (selectedStrikes.length === 0) missingItems.push('Strike');

    if (missingItems.length === 0) return '';
    if (missingItems.length === 1) return `Please select ${missingItems[0]}`;
    if (missingItems.length === 2) return `Please select ${missingItems[0]} and ${missingItems[1]}`;
    return `Please complete all sections`;
  };

  // Build scenarios for API (all combinations)
  // Exit Speed and Stock Move are no longer asked of the user — they're seeded
  // here because the backend recalculates days_to_gain per-contract anyway, and
  // the post-results Elasticity Heatmap / Simulation Settings panels are where
  // users actually explore scenarios. Direction-aware stock-move seed:
  // bullish positions → +20% (rally); bearish positions → -20% (correction).
  const buildScenarios = () => {
    if (!isFormValid()) return [];

    const isBullishPosition = direction === 'buy_call' || direction === 'sell_put';
    const moveSeed = isBullishPosition ? 'rally' : 'correction';

    const scenarios = buildIntermediateScenarios(
      {
        expirations: selectedExpirations,
        strikes: selectedStrikes,
        speeds: ['balanced'],
        moves: [moveSeed]
      },
      selectedTickers,
      direction
    );

    return scenarios;
  };

  // Handle submit (add all scenarios to queue)
  const handleSubmit = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(scenario => onAddToQueue(scenario));
    }
  };

  // Run immediately — append the form's scenarios to the queue AND fire the
  // screener in one tap (skips the "review queue, then run" step).
  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  // Toggle handlers for multi-select
  const toggleSelection = (array, setArray, key) => {
    setArray(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Determine if current direction is a put option
  const isPut = direction?.includes('put');

  // Get adjusted strike description for puts (invert the sign)
  const getAdjustedStrikeDescription = (description) => {
    if (!isPut || !description) return description;
    // For puts, invert the percentage sign in the description
    // e.g., "-20%" becomes "+20%", "+35%" becomes "-35%"
    if (description.startsWith('-')) {
      return '+' + description.slice(1);
    } else if (description.startsWith('+')) {
      return '-' + description.slice(1);
    }
    return description;
  };

  // Get summary text for accordion headers
  const getExpirationSummary = () => {
    if (selectedExpirations.length === 0) return '';
    if (selectedExpirations.length === 1) {
      return intermediatePresets.expiration[selectedExpirations[0]]?.label || '';
    }
    return `${selectedExpirations.length} selected`;
  };

  const getStrikeSummary = () => {
    if (selectedStrikes.length === 0) return '';
    if (selectedStrikes.length === 1) {
      const preset = intermediatePresets.strike[selectedStrikes[0]];
      const adjustedDesc = getAdjustedStrikeDescription(preset?.description);
      return `${preset?.label || ''} ${adjustedDesc || ''}`.trim();
    }
    return `${selectedStrikes.length} selected`;
  };

  // Calculate total scenarios for button text
  const getTotalScenarios = () => {
    if (!isFormValid()) return 0;
    return selectedExpirations.length * selectedStrikes.length;
  };

  return (
    <>
      <div className="pb-0">
        {/* Section 1: Tickers */}
        <AccordionFormSection
          title="Select Tickers"
          icon=""
          summary={selectedTickers.length > 0 ? selectedTickers.map(t => t.value).join(', ') : ''}
          isExpanded={expandedSections.includes('tickers')}
          isDisabled={isSectionDisabled('tickers')}
          isCompleted={completedSections.has('tickers')}
          onToggle={() => handleToggle('tickers')}
          isMobile={isMobile}
        >
          <MultiSelectTicker
            value={selectedTickers}
            onChange={(tickers) => {
              setSelectedTickers(tickers || []);
              if (tickers && tickers.length > 0) {
                setCompletedSections(prev => new Set(prev).add('tickers'));
              }
            }}
            placeholder="Search and select tickers (e.g., AAPL, MSFT, GOOGL)..."
            controlBackground="#1f2937"
          />
        </AccordionFormSection>

        {/* Section 2: Direction */}
        <AccordionFormSection
          title="Market Direction"
          icon=""
          summary={direction ? {
            'buy_call': 'Bullish (Buy Calls)',
            'buy_put': 'Bearish (Buy Puts)',
            'sell_put': 'Neutral/Bullish (Sell Puts)',
            'sell_call': 'Neutral/Bearish (Sell Calls)'
          }[direction] || '' : ''}
          isExpanded={expandedSections.includes('direction')}
          isDisabled={isSectionDisabled('direction')}
          isCompleted={completedSections.has('direction')}
          onToggle={() => handleToggle('direction')}
          isMobile={isMobile}
        >
          <p className="section-helper-text mb-3">Select your market outlook and position type</p>
          <div className="direction-grid grid-cols-2 gap-3">
            {/* Row 1: Buy options */}
            <button
              type="button"
              onClick={() => {
                setDirection('buy_call');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'buy_call' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bullish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Calls</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDirection('buy_put');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'buy_put' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bearish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Puts</span>
            </button>

            {/* Row 2: Sell options */}
            <button
              type="button"
              onClick={() => {
                setDirection('sell_put');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'sell_put' ? cardToggleTemplate.cssClasses.selected : ''}`}
              title="Cash-secured put: Collect premium if stock stays above strike"
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Neutral/Bullish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Sell Puts</span>
              <span className="text-xs text-gray-400 mt-1">Cash-Secured</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDirection('sell_call');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'sell_call' ? cardToggleTemplate.cssClasses.selected : ''} ${direction !== 'sell_call' ? 'border-orange-500/30' : ''}`}
              title="WARNING: Naked calls have unlimited loss potential. Only use if you fully understand the risks."
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Neutral/Bearish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Sell Calls</span>
              <span className="text-xs text-orange-400 mt-1 flex items-center gap-1">
                <span>⚠️</span> High Risk
              </span>
            </button>
          </div>
        </AccordionFormSection>

        {/* Section 3: Expiration Window */}
        <AccordionFormSection
          title="Expiration Window"
          icon=""
          summary={getExpirationSummary()}
          isExpanded={expandedSections.includes('expiration')}
          isDisabled={isSectionDisabled('expiration')}
          isCompleted={completedSections.has('expiration')}
          onToggle={() => handleToggle('expiration')}
          isMobile={isMobile}
        >
          <p className="section-helper-text">Select how far out the option expires</p>
          <div className="intermediate-preset-grid">
            {Object.entries(intermediatePresets.expiration).map(([key, preset]) => (
              <IntermediatePresetCard
                key={key}
                presetKey={key}
                label={preset.label}
                description={preset.description}
                tooltip={preset.tooltip}
                isSelected={selectedExpirations.includes(key)}
                onClick={() => {
                  toggleSelection(selectedExpirations, setSelectedExpirations, key);
                  setCompletedSections(prev => new Set(prev).add('expiration'));
                }}
              />
            ))}
          </div>
        </AccordionFormSection>

        {/* Section 4: Strike Positioning */}
        <AccordionFormSection
          title="Strike Positioning"
          icon=""
          summary={getStrikeSummary()}
          isExpanded={expandedSections.includes('strike')}
          isDisabled={isSectionDisabled('strike')}
          isCompleted={completedSections.has('strike')}
          onToggle={() => handleToggle('strike')}
          isMobile={isMobile}
        >
          <p className="section-helper-text">How aggressive should the strike be relative to stock price?</p>
          <div className="intermediate-preset-grid">
            {Object.entries(intermediatePresets.strike).map(([key, preset]) => (
              <IntermediatePresetCard
                key={key}
                presetKey={key}
                label={preset.label}
                description={getAdjustedStrikeDescription(preset.description)}
                tooltip={preset.tooltip}
                isSelected={selectedStrikes.includes(key)}
                onClick={() => {
                  toggleSelection(selectedStrikes, setSelectedStrikes, key);
                  setCompletedSections(prev => new Set(prev).add('strike'));
                }}
              />
            ))}
          </div>
        </AccordionFormSection>

      </div>

      {/* Action Buttons — Run / Add to Queue / Save */}
      {!hideButtons && (
        <div className={isMobile ? 'fixed-bottom-button' : 'inline-button'}>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRunNow}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              style={{ position: 'relative' }}
              title="Run these scenarios immediately"
            >
              {loading ? (
                <span className="spinner"></span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Play size={14} style={{ fill: 'currentColor' }} />
                  {getTotalScenarios() > 1
                    ? `Run ${getTotalScenarios()} Scenarios`
                    : 'Run Scenario'}
                </span>
              )}
              {!isFormValid() && (
                <span className={actionButtonTemplate.cssClasses.tooltip}>
                  {getDisabledTooltipMessage()}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              title="Add these scenarios to the queue without running"
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>
                  {getTotalScenarios() > 1
                    ? `+ Add ${getTotalScenarios()} to Queue`
                    : '+ Add to Queue'}
                </span>
                {queueCount > 0 && (
                  <span className="queue-badge-inline">{queueCount}</span>
                )}
              </span>
            </button>

            <button
              type="button"
              onClick={async () => {
                const scenarios = buildScenarios();
                if (scenarios.length > 0 && onSaveScenario) {
                  for (const scenario of scenarios) {
                    const success = await onSaveScenario(scenario);
                    if (!success) break;
                  }
                }
              }}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.success}`}
              title="Save this configuration for later use"
            >
              Save as preset
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default ScreenerIntermediateForm;
